import {
  doc,
  serverTimestamp,
  getDoc,
  getDocs,
  collection,
  writeBatch
} from 'firebase/firestore';
import { db } from '@lib/firebase';

import ServiceBase from '@/shared/api/base/ServiceBase';
import { ValidationError, ApiError } from '@/shared/api/errors/ApiError';
import { validateUserId, validateCourseId } from '@/shared/utils/api/validators';

const PASSING_SCORE_PERCENT = 75;
const MAX_ATTEMPTS = 3;
const RETRY_COOLDOWN_HOURS = 24;
const QUESTIONS_PER_EXAM = 50;

export interface ExamQuestion {
  id: string;
  text: string;
  correctAnswer: any;
  [key: string]: any;
}

export interface ExamAttempt {
  attemptNumber: number;
  timestamp: string | Date;
  score: number;
  correctCount: number;
  totalQuestions: number;
  passed: boolean;
  timeSpentSeconds: number;
  questions: Array<{
    questionId: string;
    studentAnswer: any;
    correctAnswer: any;
    isCorrect: boolean;
    question: string;
  }>;
}

export interface ExamEligibility {
  eligible: boolean;
  reason?: string;
  message?: string;
  hoursRemaining?: number;
}

export interface ExamStatus {
  status: string;
  attempts: number;
  maxAttempts: number;
  bestScore: number | null;
  lastAttempt?: ExamAttempt;
  passed?: boolean;
}

class ExamService extends ServiceBase {
  constructor() {
    super('ExamService');
  }

  async getRandomExamQuestions(count = QUESTIONS_PER_EXAM): Promise<ExamQuestion[]> {
    try {
      const questionsRef = collection(db, 'examQuestionBank');
      const snapshot = await getDocs(questionsRef);

      if (snapshot.size < count) {
        throw new ValidationError(`Not enough questions in bank. Need ${count}, have ${snapshot.size}`);
      }

      const allQuestions: ExamQuestion[] = [];
      snapshot.forEach(doc => {
        allQuestions.push({ id: doc.id, ...doc.data() } as ExamQuestion);
      });

      const shuffled = this._shuffleArray([...allQuestions]);
      return shuffled.slice(0, count);
    } catch (error) {
      this.logError(error, { method: 'getRandomExamQuestions', count });
      throw error;
    }
  }

  async validateExamRetakeEligibility(userId: string, courseId: string): Promise<ExamEligibility> {
    try {
      validateUserId(userId);
      validateCourseId(courseId);

      const enrollmentRef = doc(db, 'users', userId, 'enrollments', courseId);
      const enrollmentDoc = await getDoc(enrollmentRef);

      if (!enrollmentDoc.exists()) {
        return {
          eligible: false,
          reason: 'NO_ENROLLMENT',
          message: 'You must be enrolled in this course to take the exam.'
        };
      }

      const enrollment = enrollmentDoc.data();
      const attempts = (enrollment.examAttempts || []) as ExamAttempt[];

      if (attempts.length >= MAX_ATTEMPTS) {
        return {
          eligible: false,
          reason: 'MAX_ATTEMPTS_REACHED',
          message: 'You have used all 3 exam attempts. Contact support for assistance.'
        };
      }

      if (attempts.length > 0) {
        const lastAttemptTimestamp = attempts[attempts.length - 1].timestamp;
        const lastAttempt = new Date(lastAttemptTimestamp);
        const now = new Date();
        const hoursSinceLastAttempt = (now.getTime() - lastAttempt.getTime()) / (1000 * 60 * 60);

        if (hoursSinceLastAttempt < RETRY_COOLDOWN_HOURS) {
          const hoursRemaining = Math.ceil(RETRY_COOLDOWN_HOURS - hoursSinceLastAttempt);
          return {
            eligible: false,
            reason: 'COOLDOWN_ACTIVE',
            hoursRemaining,
            message: `You must wait ${hoursRemaining} more hours before retaking the exam.`
          };
        }
      }

      return { eligible: true };
    } catch (error) {
      this.logError(error, { method: 'validateExamRetakeEligibility', userId, courseId });
      throw error;
    }
  }

  async submitExam(userId: string, courseId: string, examData: { questions: ExamQuestion[]; answers: Record<string, any>; timeSpent: number }): Promise<any> {
    try {
      validateUserId(userId);
      validateCourseId(courseId);

      const { questions, answers, timeSpent } = examData;

      if (!questions || !Array.isArray(questions)) {
        throw new ValidationError('Questions must be an array');
      }
      if (!answers || typeof answers !== 'object') {
        throw new ValidationError('Answers must be an object');
      }
      if (typeof timeSpent !== 'number' || timeSpent < 0) {
        throw new ValidationError('timeSpent must be a non-negative number');
      }

      let correctCount = 0;
      const detailedResults: any[] = [];

      questions.forEach(question => {
        const studentAnswer = answers[question.id];
        const isCorrect = studentAnswer === question.correctAnswer;

        if (isCorrect) correctCount++;

        detailedResults.push({
          questionId: question.id,
          studentAnswer,
          correctAnswer: question.correctAnswer,
          isCorrect,
          question: question.text
        });
      });

      const scorePercent = Math.round((correctCount / questions.length) * 100);
      const passed = scorePercent >= PASSING_SCORE_PERCENT;

      const enrollmentRef = doc(db, 'users', userId, 'enrollments', courseId);
      const enrollmentDoc = await getDoc(enrollmentRef);

      if (!enrollmentDoc.exists()) {
        throw new ValidationError('Enrollment not found');
      }

      const enrollment = enrollmentDoc.data();
      const existingAttempts = (enrollment.examAttempts || []) as ExamAttempt[];

      if (existingAttempts.length >= MAX_ATTEMPTS) {
        throw new ApiError(
          'MAX_ATTEMPTS_REACHED',
          'You have already used all 3 exam attempts',
          400
        );
      }

      const newAttempt: ExamAttempt = {
        attemptNumber: existingAttempts.length + 1,
        timestamp: new Date().toISOString(),
        score: scorePercent,
        correctCount,
        totalQuestions: questions.length,
        passed,
        timeSpentSeconds: timeSpent,
        questions: detailedResults
      };

      let certificateIssued = false;
      const batch = writeBatch(db);

      batch.update(enrollmentRef, {
        examAttempts: [...existingAttempts, newAttempt],
        examStatus: passed ? 'PASSED' : 'FAILED',
        lastExamAttempt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });

      if (passed) {
        const totalMinutes = enrollment.totalMinutesOverall || 0;
        if (totalMinutes >= 1440) {
          certificateIssued = true;
          const certificateRef = doc(collection(db, 'certificates'));
          batch.set(certificateRef, {
            userId,
            courseId,
            issuedAt: serverTimestamp(),
            type: 'COMPLETION',
            status: 'ACTIVE'
          });

          batch.update(enrollmentRef, {
            certificateGenerated: true,
            certificateIssuedAt: serverTimestamp(),
            enrollmentStatus: 'COMPLETED'
          });
        }
      }

      // Clear active exam questions upon submission (success or final attempt)
      const activeExamRef = doc(db, 'users', userId, 'enrollments', courseId, 'activeExam', 'questions');
      batch.delete(activeExamRef);

      await batch.commit();

      this.cache.invalidate(`enrollment_${userId}_${courseId}`);
      this.cache.invalidate(`user_enrollments_${userId}`);
      this.cache.invalidate(`exam_status_${userId}_${courseId}`);
      this.cache.invalidate(`exam_attempts_${userId}_${courseId}`);

      this.log(`Exam submitted for user ${userId}, course ${courseId}, score: ${scorePercent}%`, {
        method: 'submitExam',
        score: scorePercent,
        passed,
        certificateIssued
      });

      return {
        success: true,
        passed,
        score: scorePercent,
        correctCount,
        totalQuestions: questions.length,
        attemptNumber: newAttempt.attemptNumber,
        certificate: certificateIssued ? { issued: true } : null,
        message: passed
          ? `Congratulations! You passed with ${scorePercent}%${certificateIssued ? '. Your certificate has been issued.' : ''}`
          : `You scored ${scorePercent}%. You need 75% to pass.`
      };
    } catch (error) {
      this.logError(error, { method: 'submitExam', userId, courseId });
      throw error;
    }
  }

  async getExamAttempts(userId: string, courseId: string): Promise<ExamAttempt[]> {
    try {
      validateUserId(userId);
      validateCourseId(courseId);

      const cacheKey = `exam_attempts_${userId}_${courseId}`;
      const cached = this.cache.get<ExamAttempt[]>(cacheKey);
      if (cached) return cached;

      const enrollmentRef = doc(db, 'users', userId, 'enrollments', courseId);
      const enrollmentDoc = await getDoc(enrollmentRef);

      if (!enrollmentDoc.exists()) {
        return [];
      }

      const attempts = (enrollmentDoc.data().examAttempts || []) as ExamAttempt[];
      this.cache.set(cacheKey, attempts, 5 * 60 * 1000); // 5 min cache
      return attempts;
    } catch (error) {
      this.logError(error, { method: 'getExamAttempts', userId, courseId });
      throw error;
    }
  }

  async getExamStatus(userId: string, courseId: string): Promise<ExamStatus> {
    try {
      validateUserId(userId);
      validateCourseId(courseId);

      const cacheKey = `exam_status_${userId}_${courseId}`;
      const cached = this.cache.get<ExamStatus>(cacheKey);
      if (cached) return cached;

      const enrollmentRef = doc(db, 'users', userId, 'enrollments', courseId);
      const enrollmentDoc = await getDoc(enrollmentRef);

      let statusData: ExamStatus;
      if (!enrollmentDoc.exists()) {
        statusData = {
          status: 'NOT_STARTED',
          attempts: 0,
          maxAttempts: MAX_ATTEMPTS,
          bestScore: null
        };
      } else {
        const enrollment = enrollmentDoc.data();
        const attempts = (enrollment.examAttempts || []) as ExamAttempt[];

        if (attempts.length === 0) {
          statusData = {
            status: 'NOT_STARTED',
            attempts: 0,
            maxAttempts: MAX_ATTEMPTS,
            bestScore: null
          };
        } else {
          const passedAttempt = attempts.find(a => a.passed);
          const bestScore = Math.max(...attempts.map(a => a.score));

          statusData = {
            status: enrollment.examStatus || 'FAILED',
            attempts: attempts.length,
            maxAttempts: MAX_ATTEMPTS,
            bestScore,
            lastAttempt: attempts[attempts.length - 1],
            passed: !!passedAttempt
          };
        }
      }

      this.cache.set(cacheKey, statusData, 5 * 60 * 1000); // 5 min cache
      return statusData;
    } catch (error) {
      this.logError(error, { method: 'getExamStatus', userId, courseId });
      throw error;
    }
  }

  async saveActiveExamQuestions(userId: string, courseId: string, questions: ExamQuestion[]): Promise<void> {
    try {
      validateUserId(userId);
      validateCourseId(courseId);

      const examRef = doc(db, 'users', userId, 'enrollments', courseId, 'activeExam', 'questions');
      const batch = writeBatch(db);

      batch.set(examRef, {
        questions,
        lockedAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });

      await batch.commit();
    } catch (error) {
      this.logError(error, { method: 'saveActiveExamQuestions', userId, courseId });
      throw error;
    }
  }

  async getActiveExamQuestions(userId: string, courseId: string): Promise<ExamQuestion[] | null> {
    try {
      validateUserId(userId);
      validateCourseId(courseId);

      const examRef = doc(db, 'users', userId, 'enrollments', courseId, 'activeExam', 'questions');
      const examDoc = await getDoc(examRef);

      if (!examDoc.exists()) return null;

      return examDoc.data().questions as ExamQuestion[];
    } catch (error) {
      this.logError(error, { method: 'getActiveExamQuestions', userId, courseId });
      throw error;
    }
  }

  async clearActiveExamQuestions(userId: string, courseId: string): Promise<void> {
    try {
      validateUserId(userId);
      validateCourseId(courseId);

      const examRef = doc(db, 'users', userId, 'enrollments', courseId, 'activeExam', 'questions');
      const batch = writeBatch(db);
      batch.delete(examRef);
      await batch.commit();
    } catch (error) {
      this.logError(error, { method: 'clearActiveExamQuestions', userId, courseId });
      throw error;
    }
  }

  private _shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }
}

const examServices = new ExamService();
export default examServices;

// Named exports for backward compatibility
export const getRandomExamQuestions = (count?: number) => examServices.getRandomExamQuestions(count);
export const validateExamRetakeEligibility = (userId: string, courseId: string) => examServices.validateExamRetakeEligibility(userId, courseId);
export const submitExam = (userId: string, courseId: string, examData: any) => examServices.submitExam(userId, courseId, examData);
export const getExamAttempts = (userId: string, courseId: string) => examServices.getExamAttempts(userId, courseId);
export const getExamStatus = (userId: string, courseId: string) => examServices.getExamStatus(userId, courseId);
export const saveActiveExamQuestions = (userId: string, courseId: string, questions: ExamQuestion[]) => examServices.saveActiveExamQuestions(userId, courseId, questions);
export const getActiveExamQuestions = (userId: string, courseId: string) => examServices.getActiveExamQuestions(userId, courseId);
export const clearActiveExamQuestions = (userId: string, courseId: string) => examServices.clearActiveExamQuestions(userId, courseId);
